import { Button, Checkbox, Flex, Form, Input, Spin } from "antd";
import { Translation, useTranslation } from "react-i18next";
import { useState } from "react";
import { useAddUser } from "@hooks";
import { useGetRoles } from "../../hooks/roles/useGetRoles";
import { UserRole } from "../../types/User";

interface Props {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AddUserForm = ({ setIsModalOpen }: Props) => {
  const [userForm] = Form.useForm();
  const { t } = useTranslation();
  const addUser = useAddUser();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const { data, isLoading } = useGetRoles();

  const translateRoles = (role: UserRole) => {
    switch (role.name) {
      case "Admin":
        return <Translation>{(t) => t("roles.admin")}</Translation>;
      case "DataAnalyst":
        return <Translation>{(t) => t("roles.dataAnalyst")}</Translation>;
      case "QuestionAdmin":
        return <Translation>{(t) => t("roles.questionAdmin")}</Translation>;
      case "TermAdmin":
        return <Translation>{(t) => t("roles.termAdmin")}</Translation>;
      case "ExamAdmin":
        return <Translation>{(t) => t("roles.examAdmin")}</Translation>;
      case "ExamParticipant":
        return <Translation>{(t) => t("roles.examParticipant")}</Translation>;
      case "Assessor":
        return <Translation>{(t) => t("roles.assessor")}</Translation>;
      default:
        return role.name;
    }
  };

  const onSubmit = (values: any) => {
    addUser.mutate(
      {
        ...values,
        applicationRoles: userRoles.map((role: string) => ({ id: role })),
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
        },
      }
    );
  };

  return (
    <Form layout="vertical" form={userForm} onFinish={onSubmit}>
      <Form.Item
        name={["firstName"]}
        label={t("table.name")}
        rules={[{ required: true, message: t("forms.user.firstNameDesc") }]}
      >
        <Input placeholder={t("table.name")} />
      </Form.Item>
      <Form.Item
        name={["lastName"]}
        label={t("table.lastName")}
        rules={[{ required: true, message: t("forms.user.lastNameDesc") }]}
      >
        <Input placeholder={t("table.lastName")} />
      </Form.Item>
      <Form.Item name={["oib"]} label={t("table.oib")}>
        <Input placeholder={t("forms.user.oib")} />
      </Form.Item>
      <Form.Item
        name={["email"]}
        label={t("table.email")}
        rules={[
          { required: true, message: t("forms.user.emailDesc") },
          { type: "email", message: t("forms.user.emailDesc") },
        ]}
      >
        <Input placeholder={t("table.email")} />
      </Form.Item>
      <Form.Item
        name={["password"]}
        label={t("forms.user.password")}
        tooltip={t("forms.user.passwordHint")}
        rules={[
          { required: true, message: t("forms.user.passwordDesc") },
          { min: 6, message: t("forms.user.passwordDesc") },
        ]}
      >
        <Input.Password placeholder={t("forms.user.password")} autoComplete="new-password" />
      </Form.Item>
      <Form.Item style={{ textAlign: "center" }}>
        <Spin spinning={isLoading}>
          {data && (
            <Checkbox.Group
              value={userRoles}
              onChange={(e) => {
                setUserRoles(e as string[]);
              }}
            >
              {data.map((role: UserRole) => (
                <Checkbox value={role.id} key={role.id}>
                  {translateRoles(role)}
                </Checkbox>
              ))}
            </Checkbox.Group>
          )}
        </Spin>
      </Form.Item>
      <Flex justify="end">
        <Button type="primary" htmlType="submit" loading={addUser.isPending}>
          {t("forms.category.save")}
        </Button>
      </Flex>
    </Form>
  );
};
